'use client'

import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { Typography } from '@/ui/atoms/typography/Typography'
import { FROM_SOURCES } from '@/ui/molecules/back-nav/BackNav'
import { StepJourney } from '@/ui/molecules/three-step-journey/StepJourney'
import { cn } from '@/ui/utils/style'
import { ArrowLeft, Slash } from 'lucide-react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { type ReactNode, Suspense } from 'react'
import { type PageHeroStat, PageHeroStats } from './PageHeroStats'

export interface AssetDetailsHeroProps {
  title: string
  titleAdornment?: ReactNode
  titleAction?: ReactNode
  label?: ReactNode
  subtitle?: ReactNode
  journey?: string[] | string[][] | ReactNode
  stats?: PageHeroStat[]
  statsLoading?: boolean
  statsSlot?: ReactNode
  backHref: string
  backLabel: string
  className?: string
}

export interface AssetDetailsHeroSkeletonProps {
  className?: string
}

function isMultiJourney(value: string[] | string[][] | ReactNode): value is string[][] {
  return Array.isArray(value) && value.length > 0 && Array.isArray(value[0])
}

function isSingleJourney(value: string[] | string[][] | ReactNode): value is string[] {
  return Array.isArray(value) && (value.length === 0 || typeof value[0] === 'string')
}

function BackPillButton({ href, label }: { href: string; label: string }) {
  return (
    <div className="inline-flex items-center gap-0 rounded-lg border border-white/10 p-1">
      <Link
        href={href}
        className="flex h-7 items-center justify-center gap-1 rounded-md bg-white/4 px-2 text-white text-xs transition-colors sm:h-8 sm:gap-1.5 hover:bg-white/15 sm:px-3 sm:text-sm"
      >
        <ArrowLeft className="h-4 w-4" />
        {label}
      </Link>
    </div>
  )
}

function BackPillButtonWithFromParam({ defaultHref, defaultLabel }: { defaultHref: string; defaultLabel: string }) {
  const searchParams = useSearchParams()
  const from = searchParams.get('from')
  const sourceConfig = from ? FROM_SOURCES[from] : null
  return <BackPillButton href={sourceConfig?.href ?? defaultHref} label={sourceConfig?.label ?? defaultLabel} />
}

function SuspendedBackPillButton({ href, label }: { href: string; label: string }) {
  return (
    <Suspense fallback={<BackPillButton href={href} label={label} />}>
      <BackPillButtonWithFromParam defaultHref={href} defaultLabel={label} />
    </Suspense>
  )
}

export function AssetDetailsHero({
  title,
  titleAdornment,
  titleAction,
  label,
  subtitle,
  journey,
  stats,
  statsLoading = false,
  statsSlot,
  backHref,
  backLabel,
  className,
}: AssetDetailsHeroProps) {
  // biome-ignore lint/complexity/useOptionalChain: <explanation>
  const hasStats = stats !== undefined && stats.some((s) => !s.hidden)

  const journeyElement = journey ? (
    isMultiJourney(journey) ? (
      <div className="flex items-center gap-3">
        {journey.map((steps, i) => (
          <div key={i} className="flex items-center gap-3">
            {i > 0 && <Slash className="h-3.5 w-3.5 text-white/20" />}
            <StepJourney steps={steps} className="flex" />
          </div>
        ))}
      </div>
    ) : isSingleJourney(journey) ? (
      <StepJourney steps={journey} className="flex" />
    ) : (
      journey
    )
  ) : null

  const backButton = <SuspendedBackPillButton href={backHref} label={backLabel} />

  return (
    <div
      className={cn(
        'relative mb-8 flex flex-col gap-2 overflow-hidden rounded-lg bg-gradient-to-br from-gradient-yellow/5 via-background to-gradient-green/5 p-6 sm:gap-4 md:px-10 md:py-8',
        className,
      )}
    >
      <div className="-top-24 -right-24 absolute h-48 w-48 rounded-full bg-gradient-to-br from-gradient-yellow/20 to-gradient-green/20 blur-[80px]" />
      <div className="-bottom-24 -left-24 absolute h-48 w-48 rounded-full bg-gradient-to-br from-gradient-green/20 to-gradient-yellow/20 blur-[80px]" />

      <div className="flex animate-fade-in-slow flex-col gap-2 opacity-0 md:flex-row md:items-end md:justify-between sm:gap-4">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <div className="flex items-center gap-2">
              {titleAdornment}
              <Typography variant="h1" className="text-2xl lg:text-5xl sm:text-4xl" gradient>
                {title}
              </Typography>
              {titleAction}
            </div>
            {label && <div className="mt-2 text-muted-foreground text-sm">{label}</div>}
          </div>
          {subtitle && <div className="max-w-xl text-muted-foreground text-sm sm:text-base">{subtitle}</div>}
        </div>
        {(hasStats || statsSlot) && (
          <div className="hidden shrink-0 flex-col items-end gap-4 md:flex">
            {statsSlot}
            {hasStats && <PageHeroStats stats={stats} loading={statsLoading} />}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 md:hidden">
        {(hasStats || statsSlot) && (
          <div className="mt-2 animate-fade-in-slow opacity-0" style={{ animationDelay: '120ms' }}>
            {statsSlot}
            {hasStats && <PageHeroStats stats={stats} loading={statsLoading} />}
          </div>
        )}
        <div className="mt-2 animate-fade-in-slow opacity-0 md:mt-0" style={{ animationDelay: '150ms' }}>
          {backButton}
        </div>
      </div>

      {(journeyElement || backButton) && (
        <div
          className="hidden min-h-10 animate-fade-in-slow items-center justify-between opacity-0 md:flex"
          style={{ animationDelay: '150ms' }}
        >
          <div>{journeyElement}</div>
          <div className="ml-auto">{backButton}</div>
        </div>
      )}
    </div>
  )
}

export function AssetDetailsHeroSkeleton({ className }: AssetDetailsHeroSkeletonProps) {
  return (
    <div
      className={cn(
        'relative mb-8 flex flex-col gap-2 overflow-hidden rounded-lg bg-gradient-to-br from-gradient-yellow/5 via-background to-gradient-green/5 p-6 sm:gap-4 md:px-10 md:py-8',
        className,
      )}
    >
      <div className="-top-24 -right-24 absolute h-48 w-48 rounded-full bg-gradient-to-br from-gradient-yellow/20 to-gradient-green/20 blur-[80px]" />
      <div className="-bottom-24 -left-24 absolute h-48 w-48 rounded-full bg-gradient-to-br from-gradient-green/20 to-gradient-yellow/20 blur-[80px]" />

      <div className="relative flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
        <div className="flex min-w-0 flex-col gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-10 w-10 rounded-full sm:h-14 sm:w-14" />
              <Skeleton className="h-9 w-32 lg:h-14 sm:h-12 sm:w-44" />
            </div>
            <Skeleton className="mt-2 h-4 w-48" />
          </div>
          <div className="flex max-w-xl flex-col gap-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        </div>

        <div className="flex items-center gap-3 sm:gap-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-end gap-3 sm:gap-5">
              {i > 0 && <div className="h-8 w-px bg-white/10 sm:h-10" />}
              <div className="flex flex-col items-start md:items-end">
                <Skeleton className="h-3 w-20 sm:h-4" />
                <Skeleton className="mt-1 h-4 w-16 sm:h-5 sm:w-20" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="relative hidden min-h-10 items-center justify-between md:flex">
        <div className="flex items-center gap-2">
          <Skeleton className="h-7 w-24 rounded-full" />
          <Skeleton className="h-7 w-28 rounded-full" />
        </div>
        <Skeleton className="h-10 w-32 rounded-lg" />
      </div>

      <Skeleton className="relative mt-2 h-9 w-32 rounded-lg md:hidden" />
    </div>
  )
}

export function HeroTokenImage({
  src,
  alt,
  tokenAddress,
}: {
  src: string
  alt: string
  tokenAddress: string
}) {
  return (
    <TokenImage
      src={src}
      alt={alt}
      tokenAddress={tokenAddress}
      width={40}
      height={40}
      className="h-8 w-8 rounded-full ring-1 ring-[#0a0a0c] lg:h-10 lg:w-10"
    />
  )
}
